import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { Search, Filter, ChevronLeft } from "lucide-react";
import { apiClient } from "../../services/api";
import BottomNav from "../../components/layout/BottomNav";
import ExerciseCard from "../../components/workout/ExerciseCard";
import { findGroup } from "../../data/muscleGroups";

const ALL_EQUIPMENT = ["barbell", "dumbbells", "cable", "machine", "bodyweight", "kettlebell"];
const LEVELS = ["beginner", "intermediate", "advanced"];

export default function MuscleGroup() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const group = findGroup(groupId);

  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [equipment, setEquipment] = useState(null);
  const [level, setLevel] = useState(null);
  const [submuscle, setSubmuscle] = useState(null);

  useEffect(() => {
    if (!group) return;
    const params = { limit: 200 };
    const targetMuscles = submuscle ? [submuscle] : group.muscles;
    params.muscle = targetMuscles.join(",");
    if (equipment) params.equipment = equipment;
    if (level) params.difficulty = level;
    if (q) params.search = q;
    apiClient.get("/exercises", { params }).then((r) => setItems(r.data));
  }, [group, equipment, level, submuscle, q]);

  if (!group) return <Navigate to="/workouts/gym" replace />;

  const showSubmuscleChips = group.muscles.length > 1;

  return (
    <div className="app-page" data-testid={`muscle-group-${group.id}`}>
      {/* Hero */}
      <div className="relative h-[260px]">
        <img src={group.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.85) 100%)" }}
        />
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="muscle-group-back"
          className="absolute top-4 left-4 w-10 h-10 grid place-items-center rounded-full bg-white/85 backdrop-blur"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <div className="text-[12px] uppercase tracking-wider opacity-85">Browse by group</div>
          <h1 className="font-display text-[40px] leading-[1.05]">{group.label}</h1>
          <p className="text-[14px] text-white/85 mt-1">{group.tagline}</p>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 pt-5 space-y-4">
        {/* Search */}
        <div className="flex items-center gap-2 bg-white rounded-pill h-12 px-4 border border-line">
          <Search size={18} className="text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${group.label.toLowerCase()} exercises…`}
            data-testid="muscle-search"
            className="flex-1 bg-transparent outline-none text-[14px]"
          />
        </div>

        {/* Sub-muscle chips for "Legs" / "Arms" */}
        {showSubmuscleChips && (
          <div className="flex gap-2 overflow-x-auto -mx-4 px-4">
            <Chip label="All" active={!submuscle} onClick={() => setSubmuscle(null)} />
            {group.muscles.map((m) => (
              <Chip
                key={m}
                label={m.charAt(0).toUpperCase() + m.slice(1)}
                active={submuscle === m}
                onClick={() => setSubmuscle(m)}
                testid={`submuscle-${m}`}
              />
            ))}
          </div>
        )}

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4" data-testid="equipment-chips">
          <Chip label="Any equipment" active={!equipment} onClick={() => setEquipment(null)} />
          {ALL_EQUIPMENT.map((eq) => (
            <Chip
              key={eq}
              label={eq}
              active={equipment === eq}
              onClick={() => setEquipment(equipment === eq ? null : eq)}
              testid={`eq-${eq}`}
            />
          ))}
        </div>

        <div className="flex gap-2 overflow-x-auto -mx-4 px-4" data-testid="level-chips">
          <Chip label="Any level" active={!level} onClick={() => setLevel(null)} />
          {LEVELS.map((lv) => (
            <Chip
              key={lv}
              label={lv}
              active={level === lv}
              onClick={() => setLevel(level === lv ? null : lv)}
              testid={`level-${lv}`}
            />
          ))}
        </div>

        {/* Results */}
        {items === null ? (
          <Skeleton />
        ) : items.length === 0 ? (
          <div className="bg-white rounded-[16px] p-6 text-center border border-line2">
            <Filter size={26} className="mx-auto text-ink-muted" />
            <h3 className="font-display text-[18px] mt-3">No matches</h3>
            <p className="text-[13px] text-ink-soft mt-1">Try clearing a filter or searching a different term.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="exercise-list">
            <p className="text-[12px] text-ink-muted uppercase tracking-wider">
              {items.length} exercise{items.length === 1 ? "" : "s"}
            </p>
            {items.map((ex) => (
              <ExerciseCard
                key={ex.exercise_id}
                exercise={ex}
                onClick={() => navigate(`/workouts/exercise/${ex.exercise_id}`)}
                action={<span className="text-[12px] capitalize text-ink-soft">{ex.equipment}</span>}
              />
            ))}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="px-3.5 h-9 shrink-0 rounded-pill text-[12.5px] capitalize border transition-all"
      style={{
        background: active ? "var(--accent-blue-dim)" : "white",
        borderColor: active ? "var(--accent-blue)" : "var(--border)",
        color: "var(--text-primary)",
        borderRadius: 100,
      }}
    >
      {label}
    </button>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2.5">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-24 bg-white rounded-[16px] border border-line2 animate-pulse" />
      ))}
    </div>
  );
}
